// frontend/src/pages/WorkflowsPage.tsx - COMPLETE COMPREHENSIVE VERSION
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks
import {
  useWorkflows,
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  useDeleteWorkflow,
  useWorkflowTemplates,
  useWorkflowAnalytics
} from '@/hooks/useWorkflows';

import { useOrganizations } from '@/hooks/useOrganizations';
import { useGroups } from '@/hooks/useGroups';

// Import workflow components - only import what we actually use
import { WorkflowModal } from '@/components/workflows';

// FIXED: Import API types
import type {
  Workflow,
  WorkflowStage,
  Organization,
  Group,
  User,
  NormalizedUser,
  WorkflowQueryParams,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  Priority,
  UserRole,
  StageRole,
  PaginatedResponse
} from '@/api/types';

// ===== INTERFACES =====

interface WorkflowFilters {
  search: string;
  priority: 'all' | Priority;
  status: 'all' | 'active' | 'inactive' | 'template';
  organization: string;
  sortBy: 'name' | 'createdAt' | 'priority' | 'stages';
  sortOrder: 'asc' | 'desc';
}

interface ViewMode {
  type: 'grid' | 'list' | 'table';
  itemsPerPage: number;
}

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// Format workflow status
const getWorkflowStatusText = (workflow: Workflow): string => {
  if (workflow.isTemplate) return 'Template';
  if (workflow.isActive) return 'Active';
  return 'Inactive';
};

// Get workflow status color
const getWorkflowStatusColor = (workflow: Workflow): string => {
  if (workflow.isTemplate) return 'text-blue-600 bg-blue-100';
  if (workflow.isActive) return 'text-green-600 bg-green-100';
  return 'text-gray-600 bg-gray-100';
};

// Priority colors
const getPriorityColor = (priority: Priority): string => {
  switch (priority) {
    case 'high': return 'text-red-600 bg-red-100';
    case 'medium': return 'text-yellow-600 bg-yellow-100';
    case 'low': return 'text-green-600 bg-green-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

// ===== ERROR BOUNDARY =====

class WorkflowsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('WorkflowsPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-600 mb-4">Something went wrong loading workflows.</p>
            <button 
              onClick={() => this.setState({ hasError: false })}
              className="text-blue-600 underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== WORKFLOW CARD COMPONENT =====

interface WorkflowCardProps {
  workflow: Workflow;
  currentUser: NormalizedUser | null;
  organizations: Organization[];
  onEdit: (workflow: Workflow) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  canEdit: boolean;
  canDelete: boolean;
}

const WorkflowCardComponent: React.FC<WorkflowCardProps> = React.memo(({
  workflow,
  currentUser,
  organizations,
  onEdit,
  onDelete,
  onToggleStatus,
  canEdit,
  canDelete
}) => {
  const workflowId = workflow._id || workflow.id || '';
  
  // Get organization name
  const organizationName = useMemo(() => {
    if (!workflow.organization) return 'System';
    const org = organizations.find(o => (o._id || o.id) === workflow.organization);
    return org?.name || 'Unknown Organization';
  }, [workflow.organization, organizations]);

  // Calculate stage count
  const stageCount = workflow.stages?.length || 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {workflow.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {organizationName}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cn('px-2 py-1 text-xs font-medium rounded-full', getWorkflowStatusColor(workflow))}>
            {getWorkflowStatusText(workflow)}
          </span>
        </div>
      </div>

      {/* Description */}
      {workflow.description && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
          {workflow.description}
        </p>
      )}

      {/* Metadata */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
        <div className="flex items-center space-x-4">
          <span>{stageCount} stage{stageCount !== 1 ? 's' : ''}</span>
          {workflow.priority && (
            <span className={cn('px-2 py-1 text-xs font-medium rounded-full', getPriorityColor(workflow.priority))}>
              {workflow.priority.charAt(0).toUpperCase() + workflow.priority.slice(1)}
            </span>
          )}
        </div>
        <span>{formatDate(workflow.createdAt || '')}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          {canEdit && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(workflow)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onToggleStatus(workflowId, !workflow.isActive)}
              >
                {workflow.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
        </div>
        {canDelete && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(workflowId)}
            className="text-red-600 hover:text-red-700 hover:border-red-300"
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
});

WorkflowCardComponent.displayName = 'WorkflowCardComponent';

// ===== MAIN COMPONENT =====

export const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store integration
  const { user: currentUser } = useAuthStore();
  const {
    globalError,
    setGlobalError,
    clearGlobalError,
    setApiLoading,
    clearApiLoading
  } = useAppStore();
  const { confirm } = useConfirmation();

  // Local state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [priorityFilter, setPriorityFilter] = useState<'all' | Priority>(
    (searchParams.get('priority') as Priority) || 'all'
  );
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'template'>(
    (searchParams.get('status') as 'all' | 'active' | 'inactive' | 'template') || 'all'
  );
  const [selectedOrganization, setSelectedOrganization] = useState<string>(
    searchParams.get('organization') || ''
  );
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'priority' | 'stages'>(
    (searchParams.get('sortBy') as 'name' | 'createdAt' | 'priority' | 'stages') || 'name'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc'
  );
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | undefined>(undefined);

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 500);

  // Query parameters for workflows API
  const queryParams = useMemo((): WorkflowQueryParams => {
    const params: WorkflowQueryParams = {
      page: 1,
      limit: 50,
      search: debouncedSearchTerm || undefined,
      sortBy: sortBy,
      sortOrder: sortOrder
    };

    if (statusFilter === 'active') params.isActive = true;
    if (statusFilter === 'inactive') params.isActive = false;
    if (statusFilter === 'template') params.isTemplate = true;
    if (priorityFilter !== 'all') params.priority = priorityFilter;
    if (selectedOrganization && currentUser?.role === 'implementor') {
      params.organizationId = selectedOrganization;
    }

    return params;
  }, [debouncedSearchTerm, priorityFilter, statusFilter, selectedOrganization, currentUser?.role, sortBy, sortOrder]);

  // API hooks - workflows
  const {
    data: workflowsData,
    loading: workflowsLoading,
    error: workflowsError,
    refetch: refetchWorkflows
  } = useWorkflows(queryParams);

  // API hooks - organizations (for implementors)
  const {
    data: organizationsData,
    loading: organizationsLoading,
    error: organizationsError,
    refetch: refetchOrganizations
  } = useOrganizations(
    currentUser?.role === 'implementor' ? {} : undefined,
    { 
      immediate: currentUser?.role === 'implementor'
    }
  );

  // API hooks - groups (for the selected organization)
  const groupsOrgId = useMemo(() => {
    if (currentUser?.role === 'implementor') {
      return selectedOrganization || (organizationsData?.data?.[0]?._id || organizationsData?.data?.[0]?.id);
    }
    return typeof currentUser?.organization === 'string' 
      ? currentUser.organization 
      : (currentUser?.organization as any)?._id || (currentUser?.organization as any)?.id;
  }, [currentUser, selectedOrganization, organizationsData]);

  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError,
    refetch: refetchGroups
  } = useGroups(
    groupsOrgId,
    { type: 'approval' }, // Only load approval groups
    { 
      immediate: !!groupsOrgId
    }
  );

  // Mutation hooks
  const createWorkflowMutation = useCreateWorkflow({
    onSuccess: () => {
      clearGlobalError();
      refetchWorkflows();
      setIsModalOpen(false);
      setEditingWorkflow(undefined);
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to create workflow');
    }
  });

  const updateWorkflowMutation = useUpdateWorkflow({
    onSuccess: () => {
      clearGlobalError();
      refetchWorkflows();
      setIsModalOpen(false);
      setEditingWorkflow(undefined);
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to update workflow');
    }
  });

  const deleteWorkflowMutation = useDeleteWorkflow({
    onSuccess: () => {
      clearGlobalError();
      refetchWorkflows();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to delete workflow');
    }
  });

  // Extract data safely
  const workflows = workflowsData?.data || [];
  const organizations = organizationsData?.data || [];
  const groups = groupsData?.data || [];

  // Permission checks
  const canCreate = currentUser?.role === 'admin' || currentUser?.role === 'implementor';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'implementor';
  const canDelete = currentUser?.role === 'admin';

  // Loading states
  const isLoading = workflowsLoading || 
    (currentUser?.role === 'implementor' && organizationsLoading) ||
    (!groupsOrgId && groupsLoading);

  const hasError = !!workflowsError || 
    (currentUser?.role === 'implementor' && !!organizationsError) ||
    !!groupsError;

  // Filter workflows
  const filteredWorkflows = useMemo(() => {
    return workflows.filter(workflow => {
      if (!workflow) return false;

      // Search filter
      if (debouncedSearchTerm) {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const nameMatch = workflow.name?.toLowerCase().includes(searchLower);
        const descMatch = workflow.description?.toLowerCase().includes(searchLower);
        if (!nameMatch && !descMatch) return false;
      }

      // Status filter
      if (statusFilter === 'active' && !workflow.isActive) return false;
      if (statusFilter === 'inactive' && workflow.isActive) return false;
      if (statusFilter === 'template' && !workflow.isTemplate) return false;

      // Priority filter
      if (priorityFilter !== 'all' && workflow.priority !== priorityFilter) return false;

      return true;
    });
  }, [workflows, debouncedSearchTerm, statusFilter, priorityFilter]);

  // Update URL search params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (priorityFilter !== 'all') params.set('priority', priorityFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (selectedOrganization) params.set('organization', selectedOrganization);
    if (sortBy !== 'name') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);
    
    setSearchParams(params);
  }, [searchTerm, priorityFilter, statusFilter, selectedOrganization, sortBy, sortOrder, setSearchParams]);

  // Loading state management
  useEffect(() => {
    if (isLoading) {
      setApiLoading('workflows', true);
    } else {
      setApiLoading('workflows', false);
    }
    
    return () => clearApiLoading();
  }, [isLoading, setApiLoading, clearApiLoading]);

  // Event handlers
  const handleCreateWorkflow = useCallback(() => {
    setEditingWorkflow(undefined);
    setIsModalOpen(true);
  }, []);

  const handleEditWorkflow = useCallback((workflow: Workflow) => {
    setEditingWorkflow(workflow);
    setIsModalOpen(true);
  }, []);

  const handleDeleteWorkflow = useCallback(async (workflowId: string) => {
    if (!canDelete) {
      setGlobalError('You do not have permission to delete workflows');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Workflow',
      message: 'Are you sure you want to delete this workflow? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    await deleteWorkflowMutation.mutate(workflowId);
  }, [canDelete, deleteWorkflowMutation, setGlobalError, confirm]);

  const handleToggleWorkflowStatus = useCallback(async (workflowId: string, isActive: boolean) => {
    if (!canEdit) {
      setGlobalError('You do not have permission to modify workflows');
      return;
    }

    // Find the workflow to get its name (required by backend validation)
    const workflow = workflows.find(w => (w._id || w.id) === workflowId);
    if (!workflow) {
      setGlobalError('Workflow not found');
      return;
    }

    await updateWorkflowMutation.mutate({
      id: workflowId,
      data: {
        name: workflow.name, // Required by backend validation
        isActive
      }
    });
  }, [canEdit, workflows, updateWorkflowMutation, setGlobalError]);

  const handleSaveWorkflow = useCallback(async (data: WorkflowCreateRequest | WorkflowUpdateRequest) => {
    try {
      if (editingWorkflow) {
        await updateWorkflowMutation.mutate({
          id: editingWorkflow._id || editingWorkflow.id || '',
          data: data as WorkflowUpdateRequest
        });
      } else {
        await createWorkflowMutation.mutate(data as WorkflowCreateRequest);
      }
    } catch (error: any) {
      throw error; // Let the modal handle the error display
    }
  }, [editingWorkflow, createWorkflowMutation, updateWorkflowMutation]);

  const handleRefresh = useCallback(() => {
    clearGlobalError();
    refetchWorkflows();
    if (currentUser?.role === 'implementor') {
      refetchOrganizations();
    }
    if (groupsOrgId) {
      refetchGroups();
    }
  }, [clearGlobalError, refetchWorkflows, refetchOrganizations, refetchGroups, currentUser?.role, groupsOrgId]);

  // Organization selection handler for implementors
  const handleOrganizationChange = useCallback((orgId: string) => {
    setSelectedOrganization(orgId);
    // Update URL params
    const params = new URLSearchParams(searchParams);
    if (orgId) {
      params.set('organization', orgId);
    } else {
      params.delete('organization');
    }
    setSearchParams(params);
    clearGlobalError();
  }, [clearGlobalError, searchParams, setSearchParams]);

  const handleBulkAction = useCallback((action: string, workflowIds: string[]) => {
    // Implementation for bulk actions (activate/deactivate/delete multiple workflows)
    // This would be implemented based on specific requirements
  }, []);

  // Page actions
  const actions = useMemo(() => {
    const actionList = [];
    
    if (canCreate) {
      actionList.push(
        <Button key="create" onClick={handleCreateWorkflow}>
          Create Workflow
        </Button>
      );
    }
    
    actionList.push(
      <Button key="refresh" variant="outline" onClick={handleRefresh}>
        Refresh
      </Button>
    );
    
    return actionList;
  }, [canCreate, handleCreateWorkflow, handleRefresh]);

  // Early return for implementors without organization selection
  if (currentUser?.role === 'implementor' && !selectedOrganization && organizationsData?.data && organizationsData.data.length > 0) {
    return (
      <WorkflowsErrorBoundary>
        <PageLayout 
          title="Workflows" 
          subtitle="Select an organization to manage workflows"
          loading={organizationsLoading}
        >
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Select Organization
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Choose an organization to view and manage their workflows.
            </p>
            <div className="max-w-sm mx-auto">
              <select
                value={selectedOrganization}
                onChange={(e) => handleOrganizationChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">Choose Organization</option>
                {organizations.map((org) => (
                  <option key={org._id || org.id} value={org._id || org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </PageLayout>
      </WorkflowsErrorBoundary>
    );
  }

  // Main render
  return (
    <WorkflowsErrorBoundary>
      <PageLayout 
        title="Workflows" 
        subtitle={`Manage approval workflows • ${workflows.length} total`}
        actions={actions}
        loading={isLoading && workflows.length === 0}
      >
        {/* Global Error Display */}
        {globalError && (
          <div className="mb-6">
            <ErrorAlert 
              message={globalError} 
              onClose={clearGlobalError}
            />
          </div>
        )}

        {/* API Error Display */}
        {hasError && !globalError && (
          <div className="mb-6">
            <ErrorAlert 
              message="Failed to load workflows. Please try again." 
              onClose={handleRefresh}
            />
          </div>
        )}

        {/* Filters and Controls */}
        {!isLoading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Search Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search
                </label>
                <Input
                  type="text"
                  placeholder="Search workflows..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive' | 'template')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="template">Templates</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as 'all' | Priority)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <div className="flex space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'createdAt' | 'priority' | 'stages')}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="name">Name</option>
                    <option value="createdAt">Created</option>
                    <option value="priority">Priority</option>
                    <option value="stages">Stages</option>
                  </select>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="asc">↑</option>
                    <option value="desc">↓</option>
                  </select>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    'p-2 rounded-md',
                    viewMode === 'grid'
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  )}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'p-2 rounded-md',
                    viewMode === 'list'
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  )}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {isLoading && workflows.length === 0 ? (
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="text-4xl mb-4">⚙️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No workflows found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                ? 'No workflows match your current filters. Try adjusting your search criteria.'
                : 'Create workflows to manage response approval processes.'
              }
            </p>
            {canCreate && !searchTerm && statusFilter === 'all' && priorityFilter === 'all' && (
              <Button onClick={handleCreateWorkflow}>
                Create Your First Workflow
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Workflow Grid/List */}
            <div className={cn(
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-4'
            )}>
              {filteredWorkflows.map((workflow) => {
                const workflowId = workflow._id || workflow.id || '';
                if (!workflowId) {
                  console.warn('Skipping invalid workflow:', workflow);
                  return null;
                }
                
                return (
                  <WorkflowCardComponent
                    key={workflowId}
                    workflow={workflow}
                    currentUser={currentUser}
                    organizations={organizations}
                    onEdit={handleEditWorkflow}
                    onDelete={handleDeleteWorkflow}
                    onToggleStatus={handleToggleWorkflowStatus}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                );
              })}
            </div>
            
            {/* Load More / Pagination could go here */}
          </>
        )}

        {/* Loading indicator for additional data */}
        {isLoading && workflows.length > 0 && (
          <div className="flex justify-center mt-6">
            <LoadingSpinner size="sm" />
          </div>
        )}

        {/* Workflow Modal */}
        <WorkflowModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingWorkflow(undefined);
          }}
          onSave={handleSaveWorkflow}
          workflow={editingWorkflow}
          groups={groups}
          organizations={organizations}
          currentUser={currentUser}
          isSubmitting={createWorkflowMutation.loading || updateWorkflowMutation.loading}
          selectedOrganization={selectedOrganization}
        />
      </PageLayout>
    </WorkflowsErrorBoundary>
  );
};